import { useEffect, useRef, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { getQuote } from "../http"
import { getQuoteIdFromCookie, deleteQuoteIdCookie } from "../utils/quote-cookie"
import { useMounted } from "./use-mounted"
import type { QuoteReponse } from "../http/lead-api"

type RetrieveQuoteStatus = "idle" | "loading" | "show-popup" | "done"

export function useRetrieveQuote() {
  const [status, setStatus] = useState<RetrieveQuoteStatus>("idle")
  const [petNames, setPetNames] = useState<string[]>([])
  const [quoteId, setQuoteId] = useState<string | null>(null)
  const [quoteResponse, setQuoteResponse] = useState<QuoteReponse | null>(null)
  const isMounted = useMounted()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [machineState] = useActor(newSubscriptionFlowService)
  const hasChecked = useRef(false)

  useEffect(() => {
    if (!isMounted || hasChecked.current) return
    hasChecked.current = true

    // Skip routes where the user is intentionally on a specific page —
    // the popup would be noisy and may race existing recovery logic.
    const isExcludedRoute =
      pathname?.includes(ROUTES.recap.path) ||
      pathname?.includes(ROUTES.payment.path) ||
      pathname?.includes(ROUTE_PREFIXES.welcome) ||
      pathname?.includes(ROUTE_PREFIXES.redirect)

    const cookieQuoteId = getQuoteIdFromCookie()
    const urlQuoteId = searchParams.get("quote_id")
    const stateQuoteId = machineState.context.quote?.meta?.quote_id
    const hasPetsInState = Boolean(machineState.context.quote?.pets?.length)

    if (!cookieQuoteId || urlQuoteId || stateQuoteId || hasPetsInState || isExcludedRoute) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount check driving a status state machine
      setStatus("done")
      return
    }

    setStatus("loading")

    const checkQuote = async () => {
      try {
        const quote = await getQuote(cookieQuoteId)
        const names = quote.data.map((pet) => pet.pet_name).filter(Boolean)

        if (names.length > 0) {
          setQuoteResponse(quote)
          setPetNames(names)
          setQuoteId(cookieQuoteId)
          setStatus("show-popup")
        } else {
          deleteQuoteIdCookie()
          setStatus("done")
        }
      } catch {
        deleteQuoteIdCookie()
        setStatus("done")
      }
    }

    void checkQuote()
    // One-shot: hasChecked ref gates re-runs. Intentionally reads latest
    // searchParams/machineState at first mount; adding them would re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted])

  const dismiss = () => setStatus("done")

  return {
    showPopup: status === "show-popup",
    isCheckingCookie: status === "idle" || status === "loading",
    petNames,
    quoteId,
    quoteResponse,
    dismiss,
  }
}
